import { supabase } from '../lib/supabase';

// ─── HELPERS ─────────────────────────────────────────────────────────────────

const fmt = (n) => (typeof n === 'number' ? n : parseFloat(n) || 0);

// Construir rango de fechas con límite superior correcto (incluye todo el día final)
const buildDateRange = (period, dateFrom, dateTo) => {
  const now = new Date();
  const pad = (n) => String(n)?.padStart(2, '0');
  const ymd = (d) => `${d?.getFullYear()}-${pad(d?.getMonth() + 1)}-${pad(d?.getDate())}`;

  if (period === 'custom' && dateFrom && dateTo) return { from: dateFrom, to: dateTo };
  if (period === 'today') { const t = ymd(now); return { from: t, to: t }; }
  if (period === 'yesterday') {
    const y = new Date(now); y?.setDate(now?.getDate() - 1); const t = ymd(y);
    return { from: t, to: t };
  }
  if (period === 'week') {
    const day = now?.getDay() === 0 ? 6 : now?.getDay() - 1;
    const mon = new Date(now); mon?.setDate(now?.getDate() - day);
    const sun = new Date(mon); sun?.setDate(mon?.getDate() + 6);
    return { from: ymd(mon), to: ymd(sun) };
  }
  if (period === 'month') {
    return {
      from: `${now?.getFullYear()}-${pad(now?.getMonth() + 1)}-01`,
      to: ymd(new Date(now.getFullYear(), now.getMonth() + 1, 0)),
    };
  }
  if (period === 'quarter') {
    const q = Math.floor(now?.getMonth() / 3);
    return {
      from: ymd(new Date(now.getFullYear(), q * 3, 1)),
      to: ymd(new Date(now.getFullYear(), (q + 1) * 3, 0)),
    };
  }
  // SEMESTRE: últimos 6 meses
  if (period === 'semester') {
    const sixMonthsAgo = new Date(now.getFullYear(), now.getMonth() - 5, 1);
    return {
      from: ymd(sixMonthsAgo),
      to: ymd(new Date(now.getFullYear(), now.getMonth() + 1, 0)),
    };
  }
  if (period === 'year') {
    return { from: `${now?.getFullYear()}-01-01`, to: `${now?.getFullYear()}-12-31` };
  }
  return {
    from: `${now?.getFullYear()}-${pad(now?.getMonth() + 1)}-01`,
    to: ymd(new Date(now.getFullYear(), now.getMonth() + 1, 0)),
  };
};

// Calcular coste mensual normalizado según periodicidad de imputación
const calcularCosteMensualNormalizado = (importe, periodicidadImputacion) => {
  const divisores = { MENSUAL: 1, BIMESTRAL: 2, TRIMESTRAL: 3, SEMESTRAL: 6, ANUAL: 12 };
  const divisor = divisores?.[periodicidadImputacion] || 1;
  return fmt(importe) / divisor;
};

// Calcular próximo vencimiento desde fecha y periodicidad
const calcularProximoVencimiento = (fechaBase, periodicidad) => {
  let fecha = new Date(fechaBase);
  const incrementos = { MENSUAL: 1, BIMESTRAL: 2, TRIMESTRAL: 3, SEMESTRAL: 6, ANUAL: 12 };
  const meses = incrementos?.[periodicidad] || 1;
  fecha?.setMonth(fecha?.getMonth() + meses);
  const pad = (n) => String(n)?.padStart(2, '0');
  return `${fecha?.getFullYear()}-${pad(fecha?.getMonth() + 1)}-${pad(fecha?.getDate())}`;
};

// Mapear departamento UI → valor DB
const mapDeptToDb = (dept) => {
  const map = {
    'Todos': null, 'todos': null,
    'Tienda': 'TIENDA', 'TIENDA': 'TIENDA',
    'Sala Corte': 'SALA_CORTE', 'SALA_CORTE': 'SALA_CORTE',
    'Distribución': 'DISTRIBUCION', 'DISTRIBUCION': 'DISTRIBUCION',
    'Corporativo': 'CORPORATIVO', 'CORPORATIVO': 'CORPORATIVO',
  };
  return map?.[dept] || null;
};

// ─── CENTROS DE COSTE ────────────────────────────────────────────────────────

export const centrosCosteService = {
  getAll: async () => {
    const { data, error } = await supabase?.from('fin_centros_coste')?.select('*')?.order('nombre');
    if (error) throw error;
    return data || [];
  },
  create: async (payload) => {
    const { data, error } = await supabase?.from('fin_centros_coste')?.insert(payload)?.select()?.single();
    if (error) throw error;
    return data;
  },
  update: async (id, payload) => {
    const { data, error } = await supabase?.from('fin_centros_coste')?.update({ ...payload, updated_at: new Date()?.toISOString() })?.eq('id', id)?.select()?.single();
    if (error) throw error;
    return data;
  },
};

// ─── CONFIGURACIÓN DÍAS DE APERTURA ─────────────────────────────────────────

export const configAperturaService = {
  get: async () => {
    try {
      const { data, error } = await supabase?.from('fin_config_apertura')?.select('*')?.eq('activo', true)?.single();
      if (error || !data) return { dias_apertura_mes: 26, dias_apertura_semana: 6, dias_semana: [1,2,3,4,5,6] };
      return data;
    } catch (_) {
      return { dias_apertura_mes: 26, dias_apertura_semana: 6, dias_semana: [1,2,3,4,5,6] };
    }
  },
  update: async (id, payload) => {
    const { data, error } = await supabase?.from('fin_config_apertura')
      ?.update({ ...payload, updated_at: new Date()?.toISOString() })
      ?.eq('id', id)?.select()?.single();
    if (error) throw error;
    return data;
  },
  // Calcular días abiertos transcurridos en el mes actual
  getDiasAbiertosTranscurridos: (diasSemana, hoy) => {
    const d = hoy || new Date();
    const primerDia = new Date(d.getFullYear(), d.getMonth(), 1);
    let count = 0;
    const cur = new Date(primerDia);
    while (cur <= d) {
      // getDay(): 0=Dom, 1=Lun, ..., 6=Sab
      if ((diasSemana || [1,2,3,4,5,6])?.includes(cur?.getDay())) count++;
      cur?.setDate(cur?.getDate() + 1);
    }
    return Math.max(1, count);
  },
  // Calcular días abiertos restantes en el mes actual (sin incluir hoy)
  getDiasAbiertosRestantes: (diasSemana, hoy) => {
    const d = hoy || new Date();
    const ultimoDia = new Date(d.getFullYear(), d.getMonth() + 1, 0);
    const manana = new Date(d); manana?.setDate(d?.getDate() + 1);
    let count = 0;
    const cur = new Date(manana);
    while (cur <= ultimoDia) {
      if ((diasSemana || [1,2,3,4,5,6])?.includes(cur?.getDay())) count++;
      cur?.setDate(cur?.getDate() + 1);
    }
    return count;
  },
};

// ─── GASTOS ──────────────────────────────────────────────────────────────────

// Campos GENERATED en fin_gastos — PostgreSQL los calcula automáticamente.
// NUNCA incluirlos en INSERT ni UPDATE.
const GASTOS_GENERATED_COLS = ['iva_importe', 'total', 'importe_total'];

// Whitelist de campos editables para fin_gastos
const buildGastosPayload = (raw) => {
  const allowed = [
    'fecha', 'numero_factura', 'descripcion', 'proveedor', 'proveedor_cif',
    'categoria', 'tipo', 'departamento', 'centro_coste_id',
    'base_imponible', 'iva_porcentaje',
    'estado', 'fecha_vencimiento', 'forma_pago', 'observaciones',
    'es_prevision', 'recurrente_id', 'ocr_documento_id',
    'fecha_pago', 'cuenta_id',
  ];
  const payload = {};
  for (const key of allowed) {
    if (Object.prototype?.hasOwnProperty?.call(raw, key)) {
      payload[key] = raw?.[key];
    }
  }
  return payload;
};

export const gastosService = {
  getAll: async ({ period = 'month', dateFrom, dateTo, centroCosteId, departamento, categoria, tipo, estado, search, includePrevision = true } = {}) => {
    const { from, to } = buildDateRange(period, dateFrom, dateTo);
    // Usar < día_siguiente para incluir todo el día final
    const toNext = new Date(to); toNext?.setDate(toNext?.getDate() + 1);
    const toNextStr = toNext?.toISOString()?.split('T')?.[0];

    let query = supabase?.from('fin_gastos')
      ?.select('*, centro_coste:fin_centros_coste(id,nombre,tipo)')
      ?.gte('fecha', from)
      ?.lt('fecha', toNextStr)
      ?.order('fecha', { ascending: false });

    if (centroCosteId && centroCosteId !== 'todos') query = query?.eq('centro_coste_id', centroCosteId);
    const deptDb = mapDeptToDb(departamento);
    if (deptDb) query = query?.eq('departamento', deptDb);
    if (categoria && categoria !== 'Todos') query = query?.eq('categoria', categoria);
    if (tipo && tipo !== 'Todos') query = query?.eq('tipo', tipo);
    if (estado && estado !== 'Todos') query = query?.eq('estado', estado);
    if (!includePrevision) query = query?.eq('es_prevision', false);
    if (search) query = query?.or(`descripcion.ilike.%${search}%,proveedor.ilike.%${search}%,numero_factura.ilike.%${search}%`);

    const { data, error } = await query;
    if (error) throw error;
    return data || [];
  },

  getKPIs: async ({ period = 'month', dateFrom, dateTo, centroCosteId, departamento, soloReales = false } = {}) => {
    const { from, to } = buildDateRange(period, dateFrom, dateTo);
    const toNext = new Date(to); toNext?.setDate(toNext?.getDate() + 1);
    const toNextStr = toNext?.toISOString()?.split('T')?.[0];

    let query = supabase?.from('fin_gastos')
      ?.select('tipo,base_imponible,iva_importe,total,estado,categoria,es_prevision')
      ?.gte('fecha', from)
      ?.lt('fecha', toNextStr)
      ?.neq('estado', 'CANCELADO');

    if (centroCosteId && centroCosteId !== 'todos') query = query?.eq('centro_coste_id', centroCosteId);
    const deptDb = mapDeptToDb(departamento);
    if (deptDb) query = query?.eq('departamento', deptDb);
    if (soloReales) query = query?.eq('es_prevision', false);

    const { data, error } = await query;
    if (error) throw error;
    let rows = data || [];

    const reales = rows?.filter(r => !r?.es_prevision);
    const previsiones = rows?.filter(r => r?.es_prevision && r?.estado === 'PREVISTO');

    const total = reales?.reduce((s, r) => s + fmt(r?.total), 0);
    const fijos = reales?.filter(r => r?.tipo === 'FIJO')?.reduce((s, r) => s + fmt(r?.total), 0);
    const variables = reales?.filter(r => r?.tipo === 'VARIABLE')?.reduce((s, r) => s + fmt(r?.total), 0);
    const pendientes = reales?.filter(r => r?.estado === 'PENDIENTE')?.reduce((s, r) => s + fmt(r?.total), 0);
    const vencidos = reales?.filter(r => r?.estado === 'VENCIDO')?.reduce((s, r) => s + fmt(r?.total), 0);
    const totalPrevisiones = previsiones?.reduce((s, r) => s + fmt(r?.total), 0);

    const porCategoria = {};
    reales?.forEach(r => {
      porCategoria[r.categoria] = (porCategoria?.[r?.categoria] || 0) + fmt(r?.total);
    });

    return { total, fijos, variables, pendientes, vencidos, porCategoria, count: reales?.length, totalPrevisiones };
  },

  getByCategory: async ({ period = 'month', dateFrom, dateTo, departamento } = {}) => {
    const { from, to } = buildDateRange(period, dateFrom, dateTo);
    const toNext = new Date(to); toNext?.setDate(toNext?.getDate() + 1);
    const toNextStr = toNext?.toISOString()?.split('T')?.[0];

    let query = supabase?.from('fin_gastos')
      ?.select('categoria,tipo,total,centro_coste_id,es_prevision,departamento')
      ?.gte('fecha', from)
      ?.lt('fecha', toNextStr)
      ?.eq('es_prevision', false)
      ?.neq('estado', 'CANCELADO');

    const deptDb = mapDeptToDb(departamento);
    if (deptDb) query = query?.eq('departamento', deptDb);

    const { data, error } = await query;
    if (error) throw error;
    return data || [];
  },

  create: async (payload) => {
    const safePayload = buildGastosPayload(payload);
    const { data, error } = await supabase?.from('fin_gastos')?.insert(safePayload)?.select()?.single();
    if (error) throw error;
    return data;
  },

  update: async (id, payload) => {
    const safePayload = buildGastosPayload(payload);
    const { data, error } = await supabase?.from('fin_gastos')
      ?.update({ ...safePayload, updated_at: new Date()?.toISOString() })
      ?.eq('id', id)?.select()?.single();
    if (error) throw error;

    // Si el gasto está PAGADO y se modificó base_imponible o iva_porcentaje,
    // re-fetch para obtener el nuevo total calculado por Supabase y actualizar
    // el movimiento de tesorería existente (sin crear uno nuevo).
    if (data?.estado === 'PAGADO' && (safePayload?.base_imponible !== undefined || safePayload?.iva_porcentaje !== undefined)) {
      try {
        const { data: gastoActualizado } = await supabase
          ?.from('fin_gastos')
          ?.select('id, total, estado')
          ?.eq('id', id)
          ?.single();

        if (gastoActualizado?.total) {
          // Actualizar el movimiento de tesorería asociado (exactamente 1, sin duplicar)
          await supabase
            ?.from('fin_movimientos_tesoreria')
            ?.update({
              importe: -Math.abs(gastoActualizado?.total),
              updated_at: new Date()?.toISOString(),
            })
            ?.eq('gasto_id', id)
            ?.eq('es_prevision', false);
        }
      } catch (_) {
        // No bloquear la operación principal si falla la actualización de tesorería
      }
    }

    return data;
  },

  delete: async (id) => {
    const { error } = await supabase?.from('fin_gastos')?.delete()?.eq('id', id);
    if (error) throw error;
  },

  // Marcar gasto como pagado — IDEMPOTENTE vía función SQL
  marcarPagado: async (id, fechaPago, cuentaId) => {
    const { data, error } = await supabase?.rpc('registrar_pago_gasto', {
      p_gasto_id: id,
      p_cuenta_id: cuentaId || null,
      p_fecha_pago: fechaPago || new Date()?.toISOString()?.split('T')?.[0],
    });
    if (error) throw error;
    // Devolver el gasto actualizado
    const { data: gasto } = await supabase?.from('fin_gastos')?.select('*')?.eq('id', id)?.single();
    return gasto;
  },

  checkDuplicate: async ({ proveedorCif, numeroFactura, proveedor, fecha, total }) => {
    if (proveedorCif && numeroFactura) {
      const { data } = await supabase?.from('fin_gastos')
        ?.select('id,descripcion,fecha,total')
        ?.eq('proveedor_cif', proveedorCif)?.eq('numero_factura', numeroFactura)?.limit(1);
      if (data?.length > 0) return { isDuplicate: true, existing: data?.[0], reason: 'CIF + Nº Factura' };
    }
    if (proveedor && fecha && total) {
      const { data } = await supabase?.from('fin_gastos')
        ?.select('id,descripcion,fecha,total')
        ?.ilike('proveedor', `%${proveedor}%`)?.eq('fecha', fecha)?.eq('total', total)?.limit(1);
      if (data?.length > 0) return { isDuplicate: true, existing: data?.[0], reason: 'Proveedor + Fecha + Total' };
    }
    return { isDuplicate: false };
  },
};

// ─── GASTOS RECURRENTES ──────────────────────────────────────────────────────

// Calcular base desde total con IVA incluido
export const calcularBaseDesdeTotal = (total, ivaPct) => {
  const pct = parseFloat(ivaPct) || 21;
  const base = parseFloat(total) / (1 + pct / 100);
  return Math.round(base * 100) / 100;
};

// Calcular IVA importe desde total con IVA incluido
export const calcularIvaDesdeTotal = (total, ivaPct) => {
  const base = calcularBaseDesdeTotal(total, ivaPct);
  return Math.round((parseFloat(total) - base) * 100) / 100;
};

export const gastosRecurrentesService = {
  getAll: async () => {
    const { data, error } = await supabase?.from('fin_gastos_recurrentes')
      ?.select('*, centro_coste:fin_centros_coste(id,nombre,tipo)')
      ?.order('nombre');
    if (error) throw error;
    return data || [];
  },

  create: async (payload) => {
    // Build a safe payload using only real column names
    const enriched = { ...payload };

    // Ensure dia_habitual_pago is set (canonical column); also set dia_pago for compat
    if (enriched?.dia_habitual_pago === undefined && enriched?.dia_pago !== undefined) {
      enriched.dia_habitual_pago = enriched?.dia_pago;
    }
    if (enriched?.dia_pago === undefined && enriched?.dia_habitual_pago !== undefined) {
      enriched.dia_pago = enriched?.dia_habitual_pago;
    }

    // If comes importe_total, calculate base imponible automatically
    if (enriched?.importe_total && !enriched?.importe_habitual) {
      enriched.importe_habitual = calcularBaseDesdeTotal(enriched?.importe_total, enriched?.iva_porcentaje || 21);
    } else if (enriched?.importe_habitual && !enriched?.importe_total) {
      const ivaPct = enriched?.iva_porcentaje || 21;
      enriched.importe_total = Math.round(enriched?.importe_habitual * (1 + ivaPct / 100) * 100) / 100;
    }
    if (!enriched?.proximo_vencimiento && enriched?.fecha_inicio) {
      enriched.proximo_vencimiento = enriched?.fecha_inicio;
    }
    const { data, error } = await supabase?.from('fin_gastos_recurrentes')?.insert(enriched)?.select()?.single();
    if (error) throw error;
    return data;
  },

  update: async (id, payload) => {
    const enriched = { ...payload };

    // Ensure dia_habitual_pago is set (canonical column); also set dia_pago for compat
    if (enriched?.dia_habitual_pago === undefined && enriched?.dia_pago !== undefined) {
      enriched.dia_habitual_pago = enriched?.dia_pago;
    }
    if (enriched?.dia_pago === undefined && enriched?.dia_habitual_pago !== undefined) {
      enriched.dia_pago = enriched?.dia_habitual_pago;
    }

    // Recalculate base if importe_total is provided
    if (enriched?.importe_total !== undefined) {
      enriched.importe_habitual = calcularBaseDesdeTotal(enriched?.importe_total, enriched?.iva_porcentaje || 21);
    }
    const { data, error } = await supabase?.from('fin_gastos_recurrentes')
      ?.update({ ...enriched, updated_at: new Date()?.toISOString() })
      ?.eq('id', id)?.select()?.single();
    if (error) throw error;
    return data;
  },

  delete: async (id) => {
    await supabase?.from('fin_gastos')
      ?.update({ estado: 'CANCELADO', updated_at: new Date()?.toISOString() })
      ?.eq('recurrente_id', id)?.eq('estado', 'PREVISTO');
    const { error } = await supabase?.from('fin_gastos_recurrentes')?.delete()?.eq('id', id);
    if (error) throw error;
  },

  toggleActivo: async (id, activo) => {
    const { data, error } = await supabase?.from('fin_gastos_recurrentes')
      ?.update({ activo, updated_at: new Date()?.toISOString() })
      ?.eq('id', id)?.select()?.single();
    if (error) throw error;
    return data;
  },

  // Generar previsiones idempotentes para los próximos 12 meses
  generarPrevisiones12Meses: async (recurrenteId = null) => {
    const { data, error } = await supabase?.rpc('generar_previsiones_12_meses', {
      p_recurrente_id: recurrenteId || null,
    });
    if (error) throw error;
    return data || 0;
  },

  // Generar previsiones usando la función SQL legacy (compatibilidad)
  generarPrevisiones: async (dias = 365) => {
    try {
      const result = await gastosRecurrentesService?.generarPrevisiones12Meses(null);
      return result;
    } catch (_) {
      return 0;
    }
  },

  // Actualizar importes futuros sin tocar histórico
  actualizarImporteFuturo: async (recurrenteId, nuevoImporteTotal, nuevoIvaPct, desdeFecha) => {
    const { data, error } = await supabase?.rpc('actualizar_importe_futuro', {
      p_recurrente_id: recurrenteId,
      p_nuevo_importe_total: nuevoImporteTotal,
      p_nuevo_iva_pct: nuevoIvaPct,
      p_desde_fecha: desdeFecha,
    });
    if (error) throw error;
    return data || 0;
  },

  // Obtener previsiones persistidas desde fin_gastos
  getPrevisionesDB: async (dias = 365) => {
    const hoy = new Date();
    const limite = new Date(hoy);
    limite?.setDate(hoy?.getDate() + dias);
    const pad = (n) => String(n)?.padStart(2, '0');
    const ymd = (d) => `${d?.getFullYear()}-${pad(d?.getMonth() + 1)}-${pad(d?.getDate())}`;

    const { data, error } = await supabase?.from('fin_gastos')
      ?.select('*, centro_coste:fin_centros_coste(id,nombre), recurrente:fin_gastos_recurrentes(id,nombre,periodicidad_pago,periodicidad_imputacion)')
      ?.eq('es_prevision', true)
      ?.eq('estado', 'PREVISTO')
      ?.gte('fecha_vencimiento', ymd(hoy))
      ?.lte('fecha_vencimiento', ymd(limite))
      ?.order('fecha_vencimiento', { ascending: true });
    if (error) throw error;
    return data || [];
  },

  // Obtener previsiones calculadas en memoria (para tesorería — compatibilidad)
  getPrevisiones: async (dias = 90) => {
    try {
      return await gastosRecurrentesService?.getPrevisionesDB(dias);
    } catch (_) {
      return [];
    }
  },

  // Costes fijos normalizados por centro de coste (imputación económica)
  getCostosFijosNormalizados: async () => {
    const { data, error } = await supabase?.from('fin_gastos_recurrentes')
      ?.select('*, centro_coste:fin_centros_coste(id,nombre,tipo)')
      ?.eq('activo', true)
      ?.eq('tipo', 'FIJO');
    if (error) throw error;

    let rows = data || [];
    const consolidado = { total: 0, porCentro: {}, porCategoria: {} };

    rows?.forEach(rec => {
      const periodicidadImp = rec?.periodicidad_imputacion || rec?.periodicidad || 'MENSUAL';
      const costeMensual = calcularCosteMensualNormalizado(rec?.importe_habitual, periodicidadImp);
      consolidado.total += costeMensual;

      const centroNombre = rec?.centro_coste?.nombre || 'Sin asignar';
      consolidado.porCentro[centroNombre] = (consolidado?.porCentro?.[centroNombre] || 0) + costeMensual;
      consolidado.porCategoria[rec?.categoria] = (consolidado?.porCategoria?.[rec?.categoria] || 0) + costeMensual;
    });

    return consolidado;
  },

  // Buscar recurrente que coincida con un proveedor/CIF (para OCR)
  matchRecurrente: async (proveedorTexto, cifTexto, total) => {
    const { data, error } = await supabase?.from('fin_gastos_recurrentes')
      ?.select('*, centro_coste:fin_centros_coste(id,nombre)')
      ?.eq('activo', true);
    if (error) return null;

    const texto = (proveedorTexto || '')?.toLowerCase();
    const cif = (cifTexto || '')?.toLowerCase();

    if (cif) {
      const match = (data || [])?.find(r => r?.proveedor_cif && r?.proveedor_cif?.toLowerCase() === cif);
      if (match) return match;
    }

    if (texto) {
      const match = (data || [])?.find(r =>
        r?.proveedor && texto?.includes(r?.proveedor?.toLowerCase()?.substring(0, 5))
      );
      if (match) return match;
    }

    return null;
  },

  // Sustituir previsión por gasto real (circuito OCR) — idempotente
  sustituirPrevisionPorReal: async (recurrenteId, periodoKey, gastoRealId) => {
    const { data, error } = await supabase?.rpc('sustituir_prevision_por_real', {
      p_recurrente_id: recurrenteId,
      p_periodo_key: periodoKey,
      p_gasto_real_id: gastoRealId,
    });
    if (error) throw error;
    return data;
  },
};

// ─── CUENTAS FINANCIERAS ─────────────────────────────────────────────────────

export const cuentasService = {
  getAll: async () => {
    const { data, error } = await supabase?.from('fin_cuentas')?.select('*')?.order('nombre');
    if (error) throw error;
    return data || [];
  },
  create: async (payload) => {
    const { data, error } = await supabase?.from('fin_cuentas')?.insert(payload)?.select()?.single();
    if (error) throw error;
    return data;
  },
  update: async (id, payload) => {
    const { data, error } = await supabase?.from('fin_cuentas')
      ?.update({ ...payload, updated_at: new Date()?.toISOString() })
      ?.eq('id', id)?.select()?.single();
    if (error) throw error;
    return data;
  },
};

// ─── TESORERÍA ───────────────────────────────────────────────────────────────

export const tesoreriaService = {
  getMovimientos: async ({ period = 'month', dateFrom, dateTo, cuentaId, centroCosteId, departamento, esPrevision } = {}) => {
    const { from, to } = buildDateRange(period, dateFrom, dateTo);
    const toNext = new Date(to); toNext?.setDate(toNext?.getDate() + 1);
    const toNextStr = toNext?.toISOString()?.split('T')?.[0];

    let query = supabase?.from('fin_movimientos_tesoreria')
      ?.select('*, cuenta:fin_cuentas(id,nombre,tipo), centro_coste:fin_centros_coste(id,nombre)')
      ?.gte('fecha', from)
      ?.lt('fecha', toNextStr)
      ?.order('fecha', { ascending: false });

    if (cuentaId) query = query?.eq('cuenta_id', cuentaId);
    if (centroCosteId && centroCosteId !== 'todos') query = query?.eq('centro_coste_id', centroCosteId);
    const deptDb = mapDeptToDb(departamento);
    if (deptDb) query = query?.eq('departamento', deptDb);
    if (esPrevision !== undefined) query = query?.eq('es_prevision', esPrevision);

    const { data, error } = await query;
    if (error) throw error;
    return data || [];
  },

  getKPIs: async ({ period = 'month', dateFrom, dateTo, departamento } = {}) => {
    const { from, to } = buildDateRange(period, dateFrom, dateTo);
    const toNext = new Date(to); toNext?.setDate(toNext?.getDate() + 1);
    const toNextStr = toNext?.toISOString()?.split('T')?.[0];

    let movQuery = supabase?.from('fin_movimientos_tesoreria')
      ?.select('tipo,entrada,salida,es_prevision,fecha')
      ?.gte('fecha', from)
      ?.lt('fecha', toNextStr)
      ?.eq('es_prevision', false);

    const deptDb = mapDeptToDb(departamento);
    if (deptDb) movQuery = movQuery?.eq('departamento', deptDb);

    const { data: movs, error: movErr } = await movQuery;
    if (movErr) throw movErr;

    const { data: cuentas, error: cuentasErr } = await supabase?.from('fin_cuentas')
      ?.select('saldo_actual,tipo')?.eq('activa', true);
    if (cuentasErr) throw cuentasErr;

    let rows = movs || [];
    const ingresos = rows?.filter(r => r?.tipo === 'INGRESO')?.reduce((s, r) => s + fmt(r?.entrada), 0);
    const gastos = rows?.filter(r => r?.tipo === 'GASTO')?.reduce((s, r) => s + fmt(r?.salida), 0);
    const saldoBancario = (cuentas || [])?.filter(c => c?.tipo === 'CUENTA_BANCARIA')?.reduce((s, c) => s + fmt(c?.saldo_actual), 0);
    const caja = (cuentas || [])?.filter(c => c?.tipo === 'CAJA_EFECTIVO')?.reduce((s, c) => s + fmt(c?.saldo_actual), 0);
    const tesoreriaDisponible = saldoBancario + caja;

    // Pagos pendientes: gastos PENDIENTE o VENCIDO (no previsiones)
    let gastosQuery = supabase?.from('fin_gastos')
      ?.select('total')?.in('estado', ['PENDIENTE', 'VENCIDO'])?.eq('es_prevision', false);
    if (deptDb) gastosQuery = gastosQuery?.eq('departamento', deptDb);
    const { data: gastosPend } = await gastosQuery;
    const pagosPendientes = (gastosPend || [])?.reduce((s, r) => s + fmt(r?.total), 0);

    // Cobros pendientes: facturas emitidas pendientes
    let factQuery = supabase?.from('fin_facturas')
      ?.select('pendiente')?.eq('tipo', 'EMITIDA')?.in('estado', ['PENDIENTE', 'EMITIDA']);
    if (deptDb) factQuery = factQuery?.eq('departamento', deptDb);
    const { data: factPend } = await factQuery;
    const cobrosPendientes = (factPend || [])?.reduce((s, r) => s + fmt(r?.pendiente), 0);

    return {
      saldoBancario, caja, tesoreriaDisponible, ingresos, gastos,
      cashflow: ingresos - gastos, cobrosPendientes, pagosPendientes,
      posicionNeta: cobrosPendientes - pagosPendientes,
    };
  },

  // Previsiones unificadas — fuente única: gastos recurrentes + facturas pendientes
  getPrevisiones: async (dias = 90) => {
    const hoy = new Date();
    const limite = new Date(hoy);
    limite?.setDate(hoy?.getDate() + dias);
    const pad = (n) => String(n)?.padStart(2, '0');
    const ymd = (d) => `${d?.getFullYear()}-${pad(d?.getMonth() + 1)}-${pad(d?.getDate())}`;

    // Previsiones desde gastos recurrentes (fuente única)
    const previsiones = await gastosRecurrentesService?.getPrevisiones(dias);

    // Facturas emitidas pendientes de cobro
    const { data: facturas } = await supabase?.from('fin_facturas')
      ?.select('pendiente,fecha_vencimiento,receptor')
      ?.eq('tipo', 'EMITIDA')?.in('estado', ['PENDIENTE', 'EMITIDA'])
      ?.lte('fecha_vencimiento', ymd(limite));

    const cobros = (facturas || [])?.map(f => ({
      tipo: 'COBRO', importe: fmt(f?.pendiente),
      fecha: f?.fecha_vencimiento,
      concepto: `Cobro: ${f?.receptor || ''}`, es_prevision: true,
    }));

    // Saldo actual
    const { data: cuentas } = await supabase?.from('fin_cuentas')?.select('saldo_actual')?.eq('activa', true);
    const saldoInicial = (cuentas || [])?.reduce((s, c) => s + fmt(c?.saldo_actual), 0);

    const allEvents = [
      ...previsiones?.map(p => ({ tipo: 'PAGO', importe: -p?.total, fecha: p?.fecha })),
      ...cobros?.map(c => ({ tipo: 'COBRO', importe: c?.importe, fecha: c?.fecha })),
    ]?.sort((a, b) => a?.fecha?.localeCompare(b?.fecha));

    let saldo = saldoInicial;
    const proyeccion = [];
    let fechaNegativa = null;

    allEvents?.forEach(ev => {
      saldo += ev?.importe;
      proyeccion?.push({ fecha: ev?.fecha, saldo, tipo: ev?.tipo });
      if (saldo < 0 && !fechaNegativa) fechaNegativa = ev?.fecha;
    });

    const calcSaldo = (diasMax) => {
      const limite2 = new Date(hoy); limite2?.setDate(hoy?.getDate() + diasMax);
      const evs = allEvents?.filter(e => new Date(e.fecha) <= limite2);
      return saldoInicial + evs?.reduce((s, e) => s + e?.importe, 0);
    };

    return {
      saldoInicial,
      prevision30: calcSaldo(30),
      prevision60: calcSaldo(60),
      prevision90: calcSaldo(90),
      proyeccion,
      fechaNegativa,
      previsiones,
      cobros,
    };
  },

  createMovimiento: async (payload) => {
    const { data, error } = await supabase?.from('fin_movimientos_tesoreria')?.insert(payload)?.select()?.single();
    if (error) throw error;
    return data;
  },
};

// ─── FACTURAS ────────────────────────────────────────────────────────────────

export const facturasService = {
  getAll: async ({ period = 'month', dateFrom, dateTo, tipo, estado, centroCosteId, departamento, search } = {}) => {
    const { from, to } = buildDateRange(period, dateFrom, dateTo);
    const toNext = new Date(to); toNext?.setDate(toNext?.getDate() + 1);
    const toNextStr = toNext?.toISOString()?.split('T')?.[0];

    let query = supabase?.from('fin_facturas')
      ?.select('*, centro_coste:fin_centros_coste(id,nombre)')
      ?.gte('fecha', from)
      ?.lt('fecha', toNextStr)
      ?.order('fecha', { ascending: false });

    if (tipo && tipo !== 'Todos') query = query?.eq('tipo', tipo === 'Entrada' ? 'RECIBIDA' : tipo === 'Salida' ? 'EMITIDA' : tipo);
    if (estado && estado !== 'Todos') query = query?.eq('estado', estado?.toUpperCase());
    if (centroCosteId && centroCosteId !== 'todos') query = query?.eq('centro_coste_id', centroCosteId);
    const deptDb = mapDeptToDb(departamento);
    if (deptDb) query = query?.eq('departamento', deptDb);
    if (search) query = query?.or(`numero.ilike.%${search}%,emisor.ilike.%${search}%,receptor.ilike.%${search}%`);

    const { data, error } = await query;
    if (error) throw error;
    return data || [];
  },

  // KPIs con separación facturado/cobrado/pendiente
  getKPIs: async ({ period = 'month', dateFrom, dateTo, departamento } = {}) => {
    const { from, to } = buildDateRange(period, dateFrom, dateTo);
    const toNext = new Date(to); toNext?.setDate(toNext?.getDate() + 1);
    const toNextStr = toNext?.toISOString()?.split('T')?.[0];

    let query = supabase?.from('fin_facturas')
      ?.select('tipo,base_imponible,iva_importe,total,cobrado_pagado,pendiente,estado')
      ?.gte('fecha', from)
      ?.lt('fecha', toNextStr);

    const deptDb = mapDeptToDb(departamento);
    if (deptDb) query = query?.eq('departamento', deptDb);

    const { data, error } = await query;
    if (error) throw error;
    let rows = data || [];
    const emitidas = rows?.filter(r => r?.tipo === 'EMITIDA');
    const recibidas = rows?.filter(r => r?.tipo === 'RECIBIDA');

    // Facturado = total emitido
    const facturado = emitidas?.reduce((s, r) => s + fmt(r?.total), 0);
    // Cobrado = cobrado_pagado en facturas emitidas
    const cobrado = emitidas?.reduce((s, r) => s + fmt(r?.cobrado_pagado), 0);
    // Pendiente = pendiente en facturas emitidas
    const pendiente = emitidas?.reduce((s, r) => s + fmt(r?.pendiente), 0);

    return {
      facturacionEmitida: facturado,
      facturacionRecibida: recibidas?.reduce((s, r) => s + fmt(r?.total), 0),
      baseImponibleTotal: rows?.reduce((s, r) => s + fmt(r?.base_imponible), 0),
      ivaSoportado: recibidas?.reduce((s, r) => s + fmt(r?.iva_importe), 0),
      ivaRepercutido: emitidas?.reduce((s, r) => s + fmt(r?.iva_importe), 0),
      facturasPendientes: rows?.filter(r => r?.estado === 'PENDIENTE')?.length,
      facturasVencidas: rows?.filter(r => r?.estado === 'VENCIDA')?.length,
      // Separación clara facturado/cobrado/pendiente
      importeFacturado: facturado,
      importeCobrado: cobrado,
      importePendiente: pendiente,
      importePagado: recibidas?.reduce((s, r) => s + fmt(r?.cobrado_pagado), 0),
    };
  },

  create: async (payload) => {
    const { data, error } = await supabase?.from('fin_facturas')?.insert(payload)?.select()?.single();
    if (error) throw error;
    return data;
  },

  update: async (id, payload) => {
    const { data, error } = await supabase?.from('fin_facturas')
      ?.update({ ...payload, updated_at: new Date()?.toISOString() })
      ?.eq('id', id)?.select()?.single();
    if (error) throw error;
    return data;
  },
};

// ─── REGLAS DE CLASIFICACIÓN ─────────────────────────────────────────────────

export const reglasClasificacionService = {
  getAll: async () => {
    const { data, error } = await supabase?.from('fin_reglas_clasificacion')
      ?.select('*, centro_coste:fin_centros_coste(id,nombre)')
      ?.eq('activa', true)?.order('veces_aplicada', { ascending: false });
    if (error) throw error;
    return data || [];
  },

  matchProveedor: async (proveedorTexto, cifTexto) => {
    const { data, error } = await supabase?.from('fin_reglas_clasificacion')?.select('*')?.eq('activa', true);
    if (error) return null;
    const texto = (proveedorTexto || '')?.toLowerCase();
    const cif = (cifTexto || '')?.toLowerCase();

    if (cif) {
      const cifMatch = (data || [])?.find(r => r?.proveedor_cif && r?.proveedor_cif?.toLowerCase() === cif);
      if (cifMatch) return cifMatch;
    }
    const patternMatch = (data || [])?.find(r => texto?.includes(r?.proveedor_patron?.toLowerCase()));
    return patternMatch || null;
  },

  saveRule: async (payload) => {
    const { data, error } = await supabase?.from('fin_reglas_clasificacion')
      ?.upsert(payload, { onConflict: 'proveedor_patron' })?.select()?.single();
    if (error) throw error;
    return data;
  },

  incrementUsage: async (id) => {
    const { data: current } = await supabase?.from('fin_reglas_clasificacion')
      ?.select('veces_aplicada')?.eq('id', id)?.single();
    if (current) {
      await supabase?.from('fin_reglas_clasificacion')
        ?.update({ veces_aplicada: (current?.veces_aplicada || 0) + 1 })?.eq('id', id);
    }
  },
};

// ─── COSTES FIJOS NORMALIZADOS ────────────────────────────────────────────────

export const costesFijosService = {
  // Costes fijos normalizados por imputación económica (no por pago)
  // Respeta departamento cuando se filtra
  // FIX: No usar .or() con columna de tabla relacionada — filtrar en JS después de fetch
  getNormalizados: async (departamento) => {
    // Obtener todos los recurrentes fijos activos con su centro de coste
    const { data, error } = await supabase?.from('fin_gastos_recurrentes')
      ?.select('*, centro_coste:fin_centros_coste(id,nombre,tipo)')
      ?.eq('activo', true)
      ?.eq('tipo', 'FIJO');

    if (error) throw error;

    const deptDb = mapDeptToDb(departamento);
    let rows = data || [];

    // Filtrar por departamento en JS (evita el problema de filtrar en tabla relacionada)
    if (deptDb) {
      rows = rows?.filter(rec => {
        const recDept = rec?.departamento;
        const centroDept = rec?.centro_coste?.tipo;
        return recDept === deptDb || centroDept === deptDb;
      });
    }

    const resultado = {
      TIENDA: 0, SALA_CORTE: 0, DISTRIBUCION: 0, CORPORATIVO: 0, OTRO: 0, TOTAL: 0,
      detalle: [],
    };

    rows?.forEach(rec => {
      const periodicidadImp = rec?.periodicidad_imputacion || rec?.periodicidad || 'MENSUAL';
      const costeMensual = calcularCosteMensualNormalizado(rec?.importe_habitual, periodicidadImp);
      // Determinar a qué departamento pertenece: primero departamento directo, luego tipo del centro de coste
      const centroTipo = rec?.departamento || rec?.centro_coste?.tipo || 'OTRO';

      resultado[centroTipo] = (resultado?.[centroTipo] || 0) + costeMensual;
      resultado.TOTAL += costeMensual;
      resultado?.detalle?.push({
        nombre: rec?.nombre,
        proveedor: rec?.proveedor,
        categoria: rec?.categoria,
        centroCoste: rec?.centro_coste?.nombre || 'Sin asignar',
        centroTipo,
        importeHabitual: rec?.importe_habitual,
        periodicidadPago: rec?.periodicidad_pago || rec?.periodicidad,
        periodicidadImputacion: periodicidadImp,
        costeMensualNormalizado: costeMensual,
      });
    });

    return resultado;
  },

  // Costes variables reales del periodo
  // Incluye: gastos variables fin_gastos + salarios sala de corte (sin duplicar)
  // FIX: usar base_imponible (importe gestión, sin IVA) para control de gestión
  getCostesVariables: async ({ period = 'month', dateFrom, dateTo, departamento } = {}) => {
    const { from, to } = buildDateRange(period, dateFrom, dateTo);
    const toNext = new Date(to); toNext?.setDate(toNext?.getDate() + 1);
    const toNextStr = toNext?.toISOString()?.split('T')?.[0];

    const deptDb = mapDeptToDb(departamento);

    // Gastos variables reales de fin_gastos
    let gastosQuery = supabase?.from('fin_gastos')
      ?.select('total,base_imponible,categoria,departamento')
      ?.gte('fecha', from)
      ?.lt('fecha', toNextStr)
      ?.eq('tipo', 'VARIABLE')
      ?.eq('es_prevision', false)
      ?.neq('estado', 'CANCELADO');
    if (deptDb) gastosQuery = gastosQuery?.eq('departamento', deptDb);

    const { data: gastosVar } = await gastosQuery;
    // Usar base_imponible para control de gestión (IVA deducible no es coste económico)
    let costesVariables = (gastosVar || [])?.reduce((s, r) => s + fmt(r?.base_imponible || r?.total), 0);

    // Salarios sala de corte (coste directo/variable) — solo si no hay filtro de otro dept
    if (!deptDb || deptDb === 'SALA_CORTE') {
      try {
        // Verificar si ya hay gastos de PERSONAL/SALA_CORTE en fin_gastos para evitar duplicado
        const { data: gastosSalario } = await supabase?.from('fin_gastos')
          ?.select('id')
          ?.gte('fecha', from)
          ?.lt('fecha', toNextStr)
          ?.eq('departamento', 'SALA_CORTE')
          ?.eq('categoria', 'PERSONAL')
          ?.eq('origen', 'SALA_CORTE')
          ?.eq('es_prevision', false)
          ?.limit(1);

        // Solo sumar salarios si NO están ya en fin_gastos
        if (!gastosSalario || gastosSalario?.length === 0) {
          // FIX: usar gte/lt con timestamp correcto para salarios (timestamptz)
          const { data: salarios } = await supabase?.from('salarios')
            ?.select('importe')
            ?.gte('created_at', from + 'T00:00:00.000Z')
            ?.lt('created_at', toNextStr + 'T00:00:00.000Z');
          const totalSalarios = (salarios || [])?.reduce((s, r) => s + fmt(r?.importe), 0);
          costesVariables += totalSalarios;
        }
      } catch (_) {}
    }

    return costesVariables;
  },

  // Ingresos de sala de corte desde ldd_rentabilidad_control
  // FIX: usar timestamp correcto para ldd_rentabilidad_control (timestamp sin tz)
  getIngresosSalaCorte: async ({ from, to }) => {
    const toNext = new Date(to); toNext?.setDate(toNext?.getDate() + 1);
    const toNextStr = toNext?.toISOString()?.split('T')?.[0];
    try {
      const { data: ldd } = await supabase?.from('ldd_rentabilidad_control')
        ?.select('ingresos_totales')
        ?.gte('created_at', from + 'T00:00:00')
        ?.lt('created_at', toNextStr + 'T00:00:00');
      return (ldd || [])?.reduce((s, r) => s + fmt(r?.ingresos_totales), 0);
    } catch (_) { return 0; }
  },

  // Ingresos de sala de corte desde servicios_corte (precio_servicio)
  getIngresosSalaCorteServicios: async ({ from, to }) => {
    const toNext = new Date(to); toNext?.setDate(toNext?.getDate() + 1);
    const toNextStr = toNext?.toISOString()?.split('T')?.[0];
    try {
      const { data: servicios } = await supabase?.from('servicios_corte')
        ?.select('precio_servicio')
        ?.eq('estado', 'REALIZADO')
        ?.gte('fecha', from)
        ?.lt('fecha', toNextStr);
      return (servicios || [])?.reduce((s, r) => s + fmt(r?.precio_servicio), 0);
    } catch (_) { return 0; }
  },
};

// ─── CONSOLIDACIÓN POR DEPARTAMENTO ──────────────────────────────────────────

// Forward reference: populated via Object.assign after dashboardFinancieroCorpService is defined
const dashboardFinancieroCorpServiceRef = {};

export const consolidacionDeptService = {
  // Obtener KPIs reales por departamento usando la función SQL
  // FIX: siempre cargar todos los departamentos (no solo cuando dept === 'Todos')
  getByDepartamento: async ({ period = 'month', dateFrom, dateTo } = {}) => {
    const { from, to } = buildDateRange(period, dateFrom, dateTo);

    try {
      const { data, error } = await supabase?.rpc('get_kpis_por_departamento', {
        p_fecha_inicio: from,
        p_fecha_fin: to,
      });
      if (error) throw error;
      return data || [];
    } catch (_) {
      // Fallback: calcular en JS si la función no está disponible
      return [];
    }
  },

  // Comparativa mes actual vs mes anterior por departamento
  getComparativaMeses: async (departamento) => {
    const now = new Date();
    const pad = (n) => String(n)?.padStart(2, '0');
    const ymd = (d) => `${d?.getFullYear()}-${pad(d?.getMonth() + 1)}-${pad(d?.getDate())}`;

    const mesActualFrom = `${now?.getFullYear()}-${pad(now?.getMonth() + 1)}-01`;
    const mesActualTo = ymd(new Date(now.getFullYear(), now.getMonth() + 1, 0));
    const mesAnteriorDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const mesAnteriorFrom = `${mesAnteriorDate?.getFullYear()}-${pad(mesAnteriorDate?.getMonth() + 1)}-01`;
    const mesAnteriorTo = ymd(new Date(now.getFullYear(), now.getMonth(), 0));

    const [actual, anterior] = await Promise.all([
      dashboardFinancieroCorpServiceRef?.getKPIs({ period: 'custom', dateFrom: mesActualFrom, dateTo: mesActualTo, departamento }),
      dashboardFinancieroCorpServiceRef?.getKPIs({ period: 'custom', dateFrom: mesAnteriorFrom, dateTo: mesAnteriorTo, departamento }),
    ]);

    const diff = (a, b) => ({ actual: a, anterior: b, diff: a - b, pct: b > 0 ? ((a - b) / b) * 100 : 0 });

    return {
      ingresos: diff(actual?.facturacionTotal, anterior?.facturacionTotal),
      costes: diff((actual?.costosFijosNormalizados || 0) + (actual?.costosVariables || 0), (anterior?.costosFijosNormalizados || 0) + (anterior?.costosVariables || 0)),
      resultado: diff(actual?.beneficioOperativo, anterior?.beneficioOperativo),
      margen: diff(actual?.margenBruto, anterior?.margenBruto),
    };
  },
};

// ─── DASHBOARD FINANCIERO ────────────────────────────────────────────────────

export const dashboardFinancieroCorpService = {
  // getKPIs con filtro real por departamento
  getKPIs: async ({ period = 'month', dateFrom, dateTo, departamento } = {}) => {
    const { from, to } = buildDateRange(period, dateFrom, dateTo);
    const deptDb = mapDeptToDb(departamento);

    const [gastosKPIs, facturasKPIs, tesoreriaKPIs, costosFijosNorm] = await Promise.all([
      gastosService?.getKPIs({ period, dateFrom, dateTo, departamento, soloReales: true }),
      facturasService?.getKPIs({ period, dateFrom, dateTo, departamento }),
      tesoreriaService?.getKPIs({ period, dateFrom, dateTo, departamento }),
      costesFijosService?.getNormalizados(departamento),
    ]);

    // Ingresos sala de corte (solo si no hay filtro de otro dept)
    let ingresosCorte = 0;
    if (!deptDb || deptDb === 'SALA_CORTE') {
      ingresosCorte = await costesFijosService?.getIngresosSalaCorte({ from, to });
    }

    // Fuente única de ingresos: fin_facturas (emitidas) + ldd_rentabilidad_control (sala corte sin factura)
    // Para evitar doble contabilización: si hay facturas emitidas con departamento SALA_CORTE, no sumar ldd
    let facturacionTotal = facturasKPIs?.facturacionEmitida;

    // Añadir ingresos de sala de corte solo si no están ya en fin_facturas como SALA_CORTE
    if (ingresosCorte > 0 && (!deptDb || deptDb === 'SALA_CORTE')) {
      const { data: factSala } = await supabase?.from('fin_facturas')
        ?.select('id')?.eq('tipo', 'EMITIDA')?.eq('departamento', 'SALA_CORTE')
        ?.gte('fecha', from)?.lte('fecha', to)?.limit(1);
      if (!factSala || factSala?.length === 0) {
        facturacionTotal += ingresosCorte;
      }
    }

    // Ingresos cobrados: cobrado_pagado de facturas emitidas (no asumir todo cobrado)
    // Para sala de corte sin facturas: considerar cobrado si hay datos en ldd
    const ingresosCobrados = facturasKPIs?.importeCobrado + (ingresosCorte > 0 && (!deptDb || deptDb === 'SALA_CORTE') ? ingresosCorte : 0);
    const cobrosPendientes = facturasKPIs?.importePendiente;

    // Costes fijos normalizados (imputación económica — usa base_imponible, NO total con IVA)
    const costosFijosNormalizados = deptDb
      ? (costosFijosNorm?.[deptDb] || 0)
      : (costosFijosNorm?.TOTAL || gastosKPIs?.fijos);

    // Costes variables reales (incluye salarios sala de corte sin duplicar)
    // IMPORTANTE: costes variables usan base_imponible para control de gestión
    const costosVariables = await costesFijosService?.getCostesVariables({ period, dateFrom, dateTo, departamento });

    const costesTotal = costosFijosNormalizados + costosVariables;
    // Margen de contribución = (Ingresos - Costes Variables) / Ingresos × 100
    const margenContribucion = facturacionTotal > 0
      ? ((facturacionTotal - costosVariables) / facturacionTotal) * 100 : 0;
    // Resultado operativo = Margen Contribución - Costes Fijos
    const beneficioOperativo = facturacionTotal - costesTotal;
    const margenBruto = facturacionTotal > 0 ? (beneficioOperativo / facturacionTotal) * 100 : 0;

    return {
      facturacionTotal,
      ingresosCobrados,
      cobrosPendientes,
      beneficioBruto: beneficioOperativo,
      beneficioOperativo,
      resultadoNeto: beneficioOperativo,
      margenBruto,
      tesoreriaDisponible: tesoreriaKPIs?.tesoreriaDisponible,
      saldoBancario: tesoreriaKPIs?.saldoBancario,
      pagosPendientes: tesoreriaKPIs?.pagosPendientes,
      deudaFinanciera: gastosKPIs?.pendientes,
      capitalCirculante: tesoreriaKPIs?.tesoreriaDisponible + cobrosPendientes - tesoreriaKPIs?.pagosPendientes,
      costosFijos: costosFijosNormalizados,
      costosVariables,
      margenContribucion,
      costosFijosNormalizados,
    };
  },

  getBreakEven: async ({ period = 'month', dateFrom, dateTo, departamento } = {}) => {
    const kpis = await dashboardFinancieroCorpService?.getKPIs({ period, dateFrom, dateTo, departamento });
    const { costosFijosNormalizados, facturacionTotal, costosVariables } = kpis;

    // Margen de contribución % = (Ingresos - Costes Variables) / Ingresos
    const margenContribucionPct = facturacionTotal > 0
      ? (facturacionTotal - costosVariables) / facturacionTotal : 0;

    // Punto de equilibrio = Costes Fijos / Margen Contribución %
    // Si margen <= 0 o costes fijos = 0, no calcular
    const puntoEquilibrio = (margenContribucionPct > 0 && costosFijosNormalizados > 0)
      ? costosFijosNormalizados / margenContribucionPct : 0;

    const diferencia = facturacionTotal - puntoEquilibrio;
    const pctAlcanzado = puntoEquilibrio > 0 ? (facturacionTotal / puntoEquilibrio) * 100 : 0;

    // Usar días reales de apertura desde fin_config_apertura
    const configApertura = await configAperturaService?.get();
    const diasAperturaMes = configApertura?.dias_apertura_mes || 26;
    const diasAperturaSemana = configApertura?.dias_apertura_semana || 6;
    const diasSemana = configApertura?.dias_semana || [1,2,3,4,5,6];

    // Venta diaria/semanal necesaria sobre días reales de apertura
    const ventaDiaria = diasAperturaMes > 0 ? puntoEquilibrio / diasAperturaMes : 0;
    const ventaSemanal = diasAperturaSemana > 0 ? puntoEquilibrio / (diasAperturaMes / diasAperturaSemana) : 0;
    const ventaMensual = puntoEquilibrio;

    // Proyección fin de mes usando días reales de apertura transcurridos
    const hoy = new Date();
    const diasAbiertosTranscurridos = configAperturaService?.getDiasAbiertosTranscurridos(diasSemana, hoy);
    const diasAbiertosRestantes = configAperturaService?.getDiasAbiertosRestantes(diasSemana, hoy);
    const totalDiasAbiertos = diasAbiertosTranscurridos + diasAbiertosRestantes;

    // Ritmo de venta por día abierto transcurrido
    const ritmoActual = diasAbiertosTranscurridos > 0 ? facturacionTotal / diasAbiertosTranscurridos : 0;
    // Proyección = ritmo actual × total días abiertos del mes
    const proyeccionFinMes = ritmoActual * totalDiasAbiertos;

    // Venta diaria restante necesaria = (punto equilibrio - facturación acumulada) / días abiertos restantes
    const faltaHastaEquilibrio = Math.max(0, puntoEquilibrio - facturacionTotal);
    const ventaDiariaRestante = diasAbiertosRestantes > 0 ? faltaHastaEquilibrio / diasAbiertosRestantes : 0;

    let estadoProyeccion = 'POR_DEBAJO';
    if (puntoEquilibrio <= 0) {
      estadoProyeccion = 'PENDIENTE_DATOS';
    } else if (proyeccionFinMes >= puntoEquilibrio * 1.1) {
      estadoProyeccion = 'EN_OBJETIVO';
    } else if (proyeccionFinMes >= puntoEquilibrio * 0.9) {
      estadoProyeccion = 'RIESGO';
    }

    return {
      costosFijos: costosFijosNormalizados,
      costosVariables, facturacionTotal,
      margenContribucionPct: margenContribucionPct * 100,
      puntoEquilibrio,
      diferencia,
      faltaHastaEquilibrio,
      pctAlcanzado,
      ventaDiaria,
      ventaSemanal,
      ventaMensual,
      ventaDiariaRestante,
      proyeccionFinMes,
      estadoProyeccion,
      diasAperturaMes,
      diasAperturaSemana,
      diasAbiertosTranscurridos,
      diasAbiertosRestantes,
    };
  },

  getComparativa: async (departamento) => {
    const now = new Date();
    const pad = (n) => String(n)?.padStart(2, '0');
    const ymd = (d) => `${d?.getFullYear()}-${pad(d?.getMonth() + 1)}-${pad(d?.getDate())}`;

    const mesActualFrom = `${now?.getFullYear()}-${pad(now?.getMonth() + 1)}-01`;
    const mesActualTo = ymd(new Date(now.getFullYear(), now.getMonth() + 1, 0));
    // Mes anterior: si estamos en enero, ir a diciembre del año anterior
    const mesAnteriorDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const mesAnteriorFrom = `${mesAnteriorDate?.getFullYear()}-${pad(mesAnteriorDate?.getMonth() + 1)}-01`;
    const mesAnteriorTo = ymd(new Date(now.getFullYear(), now.getMonth(), 0));

    const [actual, anterior] = await Promise.all([
      dashboardFinancieroCorpService?.getKPIs({ period: 'custom', dateFrom: mesActualFrom, dateTo: mesActualTo, departamento }),
      dashboardFinancieroCorpService?.getKPIs({ period: 'custom', dateFrom: mesAnteriorFrom, dateTo: mesAnteriorTo, departamento }),
    ]);

    const diff = (a, b) => ({ actual: a, anterior: b, diff: a - b, pct: b > 0 ? ((a - b) / b) * 100 : 0 });

    return {
      ventas: diff(actual?.facturacionTotal, anterior?.facturacionTotal),
      costes: diff(actual?.costosFijos + actual?.costosVariables, anterior?.costosFijos + anterior?.costosVariables),
      margen: diff(actual?.margenBruto, anterior?.margenBruto),
      beneficio: diff(actual?.resultadoNeto, anterior?.resultadoNeto),
      tesoreria: diff(actual?.tesoreriaDisponible, anterior?.tesoreriaDisponible),
    };
  },

  getAlertas: async (departamento) => {
    const alertas = [];
    const hoy = new Date()?.toISOString()?.split('T')?.[0];
    const en7dias = new Date(); en7dias?.setDate(en7dias?.getDate() + 7);
    const en7str = en7dias?.toISOString()?.split('T')?.[0];
    const deptDb = mapDeptToDb(departamento);

    let vencidasQ = supabase?.from('fin_gastos')?.select('id')?.eq('estado', 'VENCIDO')?.eq('es_prevision', false);
    if (deptDb) vencidasQ = vencidasQ?.eq('departamento', deptDb);
    const { data: vencidas } = await vencidasQ;
    if ((vencidas || [])?.length > 0) {
      alertas?.push({ tipo: 'FACTURA_VENCIDA', mensaje: `${vencidas?.length} factura(s) vencida(s)`, nivel: 'error', count: vencidas?.length });
    }

    let proximosQ = supabase?.from('fin_gastos')?.select('id')?.eq('estado', 'PENDIENTE')?.eq('es_prevision', false)
      ?.gte('fecha_vencimiento', hoy)?.lte('fecha_vencimiento', en7str);
    if (deptDb) proximosQ = proximosQ?.eq('departamento', deptDb);
    const { data: proximos } = await proximosQ;
    if ((proximos || [])?.length > 0) {
      alertas?.push({ tipo: 'PAGO_PROXIMO', mensaje: `${proximos?.length} pago(s) en los próximos 7 días`, nivel: 'warning', count: proximos?.length });
    }

    let cobrosVencidosQ = supabase?.from('fin_facturas')?.select('id')?.eq('tipo', 'EMITIDA')?.eq('estado', 'VENCIDA');
    if (deptDb) cobrosVencidosQ = cobrosVencidosQ?.eq('departamento', deptDb);
    const { data: cobrosVencidos } = await cobrosVencidosQ;
    if ((cobrosVencidos || [])?.length > 0) {
      alertas?.push({ tipo: 'COBRO_VENCIDO', mensaje: `${cobrosVencidos?.length} cobro(s) vencido(s)`, nivel: 'error', count: cobrosVencidos?.length });
    }

    const { data: prevProximas } = await supabase?.from('fin_gastos')
      ?.select('id')?.eq('es_prevision', true)?.eq('estado', 'PREVISTO')
      ?.gte('fecha', hoy)?.lte('fecha', en7str);
    if ((prevProximas || [])?.length > 0) {
      alertas?.push({ tipo: 'PREVISION_PROXIMA', mensaje: `${prevProximas?.length} previsión(es) próxima(s) sin confirmar`, nivel: 'info', count: prevProximas?.length });
    }

    return alertas;
  },

  // ─── CONTABILIDAD ANALÍTICA DE GESTIÓN ───────────────────────────────────
  // Mismo motor que el Dashboard. Alimenta fin-contabilidad/index.jsx.
  // Devuelve la estructura de la Cuenta de Resultados con datos reales.
  // REGLAS:
  //   - 0 solo cuando fuente existe y resultado es realmente 0
  //   - null/pendiente cuando no hay fuente fiable
  //   - IVA: usar base_imponible para gestión (no total)
  //   - Gastos recurrentes: usar periodicidad_imputacion (no pago)
  //   - No duplicar: salarios sala no se suman si ya están en fin_gastos
  //   - SALARIOS: public.salarios → EXCLUSIVAMENTE SALA_CORTE y CONSOLIDADO
  //   - CORPORATIVO: solo gastos con departamento=CORPORATIVO
  //   - TIENDA/DISTRIBUCION: pendiente hasta Epelsa/Contasimple
  getContabilidadAnalitica: async ({ period = 'month', dateFrom, dateTo, departamento } = {}) => {
    const { from, to } = buildDateRange(period, dateFrom, dateTo);
    const deptDb = mapDeptToDb(departamento);
    const toNext = new Date(to); toNext?.setDate(toNext?.getDate() + 1);
    const toNextStr = toNext?.toISOString()?.split('T')?.[0];

    // ── 1. INGRESOS ──────────────────────────────────────────────────────────
    // Fuente primaria: fin_facturas EMITIDAS (base_imponible para gestión)
    let factQuery = supabase?.from('fin_facturas')
      ?.select('tipo,base_imponible,iva_importe,total,cobrado_pagado,pendiente,estado,departamento')
      ?.gte('fecha', from)?.lt('fecha', toNextStr)?.eq('tipo', 'EMITIDA');
    if (deptDb) factQuery = factQuery?.eq('departamento', deptDb);
    const { data: facturas } = await factQuery;
    const factRows = facturas || [];

    // Desglose ingresos por departamento desde facturas
    const ventasSalaFacturas = factRows?.filter(f => f?.departamento === 'SALA_CORTE')?.reduce((s, f) => s + fmt(f?.base_imponible || f?.total), 0);
    const ventasTiendaFacturas = factRows?.filter(f => f?.departamento === 'TIENDA')?.reduce((s, f) => s + fmt(f?.base_imponible || f?.total), 0);
    const ventasDistribucionFacturas = factRows?.filter(f => f?.departamento === 'DISTRIBUCION')?.reduce((s, f) => s + fmt(f?.base_imponible || f?.total), 0);
    const otrosIngresosFacturas = factRows?.filter(f => !['SALA_CORTE','TIENDA','DISTRIBUCION']?.includes(f?.departamento))?.reduce((s, f) => s + fmt(f?.base_imponible || f?.total), 0);

    // Ingresos sala de corte desde ldd_rentabilidad_control (fuente real cuando no hay facturas)
    // SOLO para SALA_CORTE o CONSOLIDADO (no para CORPORATIVO, TIENDA, DISTRIBUCION)
    let ingresosLdd = 0;
    let ingresosLddCount = 0;
    let ingresosServiciosCorte = 0;
    if (!deptDb || deptDb === 'SALA_CORTE') {
      // Solo usar ldd si no hay facturas SALA_CORTE para evitar duplicación
      if (ventasSalaFacturas === 0) {
        ingresosLdd = await costesFijosService?.getIngresosSalaCorte({ from, to });
        // Contar registros para saber si hay datos reales
        try {
          const { data: lddCount, count: lddCountNum } = await supabase?.from('ldd_rentabilidad_control')
            ?.select('id', { count: 'exact', head: true })
            ?.gte('created_at', from + 'T00:00:00')
            ?.lt('created_at', toNextStr + 'T00:00:00');
          ingresosLddCount = lddCountNum || 0;
        } catch (_) {}
        // Servicios de corte como fuente adicional
        ingresosServiciosCorte = await costesFijosService?.getIngresosSalaCorteServicios({ from, to });
      }
    }

    // Determinar estado de cada fuente de ingresos
    const ventasSala = ventasSalaFacturas + ingresosLdd + ingresosServiciosCorte;
    const ventasTienda = ventasTiendaFacturas;
    const ventasDistribucion = ventasDistribucionFacturas;
    const otrosIngresos = otrosIngresosFacturas;

    // Estado de cobertura de ingresos
    // Tienda: pendiente Epelsa (no hay facturas ni fuente alternativa fiable)
    // Solo marcar pendiente si estamos viendo TIENDA o CONSOLIDADO
    const tiendaPendiente = (!deptDb || deptDb === 'TIENDA');
    // Distribución: pendiente Contasimple — solo si vemos DISTRIBUCION o CONSOLIDADO
    const distribucionPendiente = (!deptDb || deptDb === 'DISTRIBUCION');
    // Sala: tiene datos reales si hay ldd o facturas
    const salaConDatos = (!deptDb || deptDb === 'SALA_CORTE') && (ventasSala > 0 || ingresosLddCount > 0);

    const totalIngresos = ventasSala + ventasTienda + ventasDistribucion + otrosIngresos;

    // ── 2. COSTE DE MERCANCÍA VENDIDA (COGS) ─────────────────────────────────
    // TIENDA: pendiente Epelsa — no inventar COGS
    // SALA_CORTE: coste de materia prima desde ldd_rentabilidad_control
    // DISTRIBUCION: pendiente Contasimple
    // CORPORATIVO: no tiene COGS (no vende mercancía directamente)
    let cogsSala = 0;
    let cogsSalaPendiente = false;
    let cogsTienda = 0;
    let cogsTiendaPendiente = false;
    let cogsDistribucion = 0;
    let cogsDistribucionPendiente = false;

    // COGS Sala desde ldd_rentabilidad_control (coste_jamon_total + coste_plasticos_total)
    // SOLO para SALA_CORTE o CONSOLIDADO
    if (!deptDb || deptDb === 'SALA_CORTE') {
      try {
        const { data: lddCogs } = await supabase?.from('ldd_rentabilidad_control')
          ?.select('coste_jamon_total,coste_plasticos_total,coste_total')
          ?.gte('created_at', from + 'T00:00:00')
          ?.lt('created_at', toNextStr + 'T00:00:00');
        if (lddCogs && lddCogs?.length > 0) {
          cogsSala = (lddCogs || [])?.reduce((s, r) => {
            const costeMercancia = fmt(r?.coste_jamon_total) + fmt(r?.coste_plasticos_total);
            return s + (costeMercancia > 0 ? costeMercancia : fmt(r?.coste_total));
          }, 0);
        }
      } catch (_) {
        cogsSalaPendiente = true;
      }
    }

    // COGS Tienda: pendiente Epelsa — solo si vemos TIENDA o CONSOLIDADO
    if (!deptDb || deptDb === 'TIENDA') {
      cogsTiendaPendiente = true;
    }

    // COGS Distribución: pendiente Contasimple — solo si vemos DISTRIBUCION o CONSOLIDADO
    if (!deptDb || deptDb === 'DISTRIBUCION') {
      cogsDistribucionPendiente = true;
    }

    const cogsPendiente = cogsTiendaPendiente || cogsDistribucionPendiente;
    const totalCOGS = cogsSala + cogsTienda + cogsDistribucion;

    // ── 3. MARGEN BRUTO ──────────────────────────────────────────────────────
    const margenBruto = totalIngresos - totalCOGS;
    const margenBrutoPendiente = cogsPendiente && (ventasTienda > 0 || ventasDistribucion > 0);

    // ── 4. GASTOS DE PERSONAL ────────────────────────────────────────────────
    // REGLA CRÍTICA: public.salarios → EXCLUSIVAMENTE SALA_CORTE y CONSOLIDADO
    // NUNCA incluir salarios de public.salarios en CORPORATIVO, TIENDA o DISTRIBUCION
    let salariosSala = 0;
    let salariosSalaFuente = 'PENDIENTE';

    // Solo cargar salarios si el departamento es SALA_CORTE o CONSOLIDADO (null)
    if (!deptDb || deptDb === 'SALA_CORTE') {
      try {
        const { data: salarios } = await supabase?.from('salarios')
          ?.select('importe')
          ?.gte('created_at', from + 'T00:00:00.000Z')
          ?.lt('created_at', toNextStr + 'T00:00:00.000Z');
        salariosSala = (salarios || [])?.reduce((s, r) => s + fmt(r?.importe), 0);
        salariosSalaFuente = 'REAL'; // public.salarios tiene datos
      } catch (_) {}
    }
    // Para CORPORATIVO, TIENDA, DISTRIBUCION: salariosSala = 0 siempre

    // Gastos de personal desde fin_gastos (SEGURIDAD_SOCIAL, PERSONAL)
    // Filtrados por departamento — solo del dept seleccionado
    let gastosPersonalQuery = supabase?.from('fin_gastos')
      ?.select('categoria,base_imponible,total,departamento')
      ?.gte('fecha', from)?.lt('fecha', toNextStr)
      ?.eq('es_prevision', false)?.neq('estado', 'CANCELADO')
      ?.in('categoria', ['SEGURIDAD_SOCIAL', 'PERSONAL']);
    if (deptDb) gastosPersonalQuery = gastosPersonalQuery?.eq('departamento', deptDb);
    const { data: gastosPersonal } = await gastosPersonalQuery;
    const ssEmpresa = (gastosPersonal || [])?.filter(g => g?.categoria === 'SEGURIDAD_SOCIAL')?.reduce((s, g) => s + fmt(g?.base_imponible || g?.total), 0);
    const otroPersonal = (gastosPersonal || [])?.filter(g => g?.categoria === 'PERSONAL')?.reduce((s, g) => s + fmt(g?.base_imponible || g?.total), 0);

    // Evitar duplicar salarios sala si ya están en fin_gastos como PERSONAL/SALA_CORTE
    // Solo aplica cuando vemos SALA_CORTE o CONSOLIDADO
    let salariosSalaFinal = salariosSala;
    if (!deptDb || deptDb === 'SALA_CORTE') {
      try {
        const { data: gastosSalarioExistentes } = await supabase?.from('fin_gastos')
          ?.select('id')
          ?.gte('fecha', from)?.lt('fecha', toNextStr)
          ?.eq('departamento', 'SALA_CORTE')
          ?.eq('categoria', 'PERSONAL')
          ?.eq('es_prevision', false)
          ?.limit(1);
        if (gastosSalarioExistentes && gastosSalarioExistentes?.length > 0) {
          salariosSalaFinal = 0; // Ya contabilizados en fin_gastos
        }
      } catch (_) {}
    }
    // Para CORPORATIVO/TIENDA/DISTRIBUCION: salariosSalaFinal ya es 0

    const totalPersonal = salariosSalaFinal + ssEmpresa + otroPersonal;

    // ── 5. GASTOS OPERATIVOS ─────────────────────────────────────────────────
    // FUENTE 1: Gastos fijos normalizados por periodicidad_imputacion (recurrentes)
    //           Filtrados por departamento del recurrente
    // FUENTE 2: Gastos variables reales del periodo desde fin_gastos
    //           Filtrados por departamento
    // CORPORATIVO: solo gastos con departamento=CORPORATIVO
    // SALA_CORTE: solo gastos con departamento=SALA_CORTE
    const costosFijosNorm = await costesFijosService?.getNormalizados(departamento);
    const detalleGastos = costosFijosNorm?.detalle || [];

    const CATS_OPERATIVAS = ['ALQUILER','ELECTRICIDAD','AGUA','GESTORIA','SEGUROS','TELEFONIA_INTERNET','SOFTWARE','TRANSPORTE','COMBUSTIBLE','MANTENIMIENTO','REPARACIONES','PUBLICIDAD','MATERIAL','SERVICIOS_PROFESIONALES','OTROS'];

    const getGastoCategoriaNorm = (cat) => detalleGastos?.filter(d => d?.categoria === cat)?.reduce((s, d) => s + (d?.costeMensualNormalizado || 0), 0);

    // Gastos operativos normalizados (recurrentes fijos) — ya filtrados por dept en getNormalizados
    const alquilerNorm = getGastoCategoriaNorm('ALQUILER');
    const electricidadNorm = getGastoCategoriaNorm('ELECTRICIDAD');
    const aguaNorm = getGastoCategoriaNorm('AGUA');
    const gestoriaNorm = getGastoCategoriaNorm('GESTORIA');
    const segurosNorm = getGastoCategoriaNorm('SEGUROS');
    const softwareNorm = getGastoCategoriaNorm('SOFTWARE') + getGastoCategoriaNorm('TELEFONIA_INTERNET');
    const transporteNorm = getGastoCategoriaNorm('TRANSPORTE') + getGastoCategoriaNorm('COMBUSTIBLE');
    const publicidadNorm = getGastoCategoriaNorm('PUBLICIDAD');
    const otrosNorm = detalleGastos?.filter(d => !['ALQUILER','ELECTRICIDAD','AGUA','GESTORIA','SEGUROS','SOFTWARE','TELEFONIA_INTERNET','TRANSPORTE','COMBUSTIBLE','PUBLICIDAD','SEGURIDAD_SOCIAL','PERSONAL','COMISIONES_BANCARIAS','IMPUESTOS','MERCANCIA']?.includes(d?.categoria))?.reduce((s, d) => s + (d?.costeMensualNormalizado || 0), 0);

    // Gastos variables reales del periodo — filtrados estrictamente por departamento
    let gastosVarQuery = supabase?.from('fin_gastos')
      ?.select('categoria,base_imponible,total,recurrente_id,departamento')
      ?.gte('fecha', from)?.lt('fecha', toNextStr)
      ?.eq('es_prevision', false)?.neq('estado', 'CANCELADO')
      ?.in('categoria', CATS_OPERATIVAS);
    if (deptDb) gastosVarQuery = gastosVarQuery?.eq('departamento', deptDb);
    const { data: gastosVarReal } = await gastosVarQuery;

    // Solo sumar los que NO tienen recurrente_id (los recurrentes ya están en normalizados)
    const gastosVarSinRecurrente = (gastosVarReal || [])?.filter(g => !g?.recurrente_id);
    const gastosVarPorCategoria = {};
    gastosVarSinRecurrente?.forEach(g => {
      gastosVarPorCategoria[g?.categoria] = (gastosVarPorCategoria?.[g?.categoria] || 0) + fmt(g?.base_imponible || g?.total);
    });

    // Combinar normalizados + variables reales sin recurrente
    const alquiler = alquilerNorm + (gastosVarPorCategoria?.ALQUILER || 0);
    const electricidad = electricidadNorm + (gastosVarPorCategoria?.ELECTRICIDAD || 0);
    const agua = aguaNorm + (gastosVarPorCategoria?.AGUA || 0);
    const gestoria = gestoriaNorm + (gastosVarPorCategoria?.GESTORIA || 0);
    const seguros = segurosNorm + (gastosVarPorCategoria?.SEGUROS || 0);
    const software = softwareNorm + (gastosVarPorCategoria?.SOFTWARE || 0) + (gastosVarPorCategoria?.TELEFONIA_INTERNET || 0);
    const transporte = transporteNorm + (gastosVarPorCategoria?.TRANSPORTE || 0) + (gastosVarPorCategoria?.COMBUSTIBLE || 0);
    const publicidad = publicidadNorm + (gastosVarPorCategoria?.PUBLICIDAD || 0);
    const otrosGastos = otrosNorm + Object.entries(gastosVarPorCategoria)
      ?.filter(([cat]) => !['ALQUILER','ELECTRICIDAD','AGUA','GESTORIA','SEGUROS','SOFTWARE','TELEFONIA_INTERNET','TRANSPORTE','COMBUSTIBLE','PUBLICIDAD']?.includes(cat))
      ?.reduce((s, [, v]) => s + v, 0);

    const totalGastosOperativos = alquiler + electricidad + agua + gestoria + seguros + software + transporte + publicidad + otrosGastos;

    // ── 6. GASTOS FINANCIEROS ────────────────────────────────────────────────
    let gastosFinQuery = supabase?.from('fin_gastos')
      ?.select('base_imponible,total,categoria')
      ?.gte('fecha', from)?.lt('fecha', toNextStr)
      ?.eq('es_prevision', false)?.neq('estado', 'CANCELADO')
      ?.in('categoria', ['COMISIONES_BANCARIAS', 'IMPUESTOS']);
    if (deptDb) gastosFinQuery = gastosFinQuery?.eq('departamento', deptDb);
    const { data: gastosFin } = await gastosFinQuery;
    const comisionesBancarias = (gastosFin || [])?.filter(g => g?.categoria === 'COMISIONES_BANCARIAS')?.reduce((s, g) => s + fmt(g?.base_imponible || g?.total), 0);
    const totalGastosFinancieros = comisionesBancarias;
    const gastosFinancierosConFuente = true;

    // ── 7. RESULTADOS ────────────────────────────────────────────────────────
    const resultadoOperativo = margenBruto - totalPersonal - totalGastosOperativos;
    const resultadoAnteImpuestos = resultadoOperativo - totalGastosFinancieros;

    // ── 8. COBERTURA DE DATOS ────────────────────────────────────────────────
    // CHECK VERDE solo si la fuente necesaria está realmente conectada y es fiable
    // Estados: COMPLETO, PARCIAL, PENDIENTE
    const componentesCobertura = [];

    // Gastos operativos: COMPLETO si hay recurrentes configurados o gastos reales
    componentesCobertura?.push({
      nombre: 'Gastos operativos',
      estado: (totalGastosOperativos > 0 || detalleGastos?.length > 0) ? 'COMPLETO' : 'PENDIENTE',
      fuente: 'fin_gastos + fin_gastos_recurrentes',
    });

    // Gastos recurrentes: COMPLETO si hay recurrentes activos
    componentesCobertura?.push({
      nombre: 'Gastos recurrentes',
      estado: detalleGastos?.length > 0 ? 'COMPLETO' : 'PENDIENTE',
      fuente: 'fin_gastos_recurrentes',
    });

    // Salarios Sala: solo relevante para SALA_CORTE y CONSOLIDADO
    if (!deptDb || deptDb === 'SALA_CORTE') {
      componentesCobertura?.push({
        nombre: 'Salarios Sala',
        estado: salariosSala > 0 ? 'COMPLETO' : 'PENDIENTE',
        fuente: 'public.salarios',
      });
    }

    // Ventas Sala: solo relevante para SALA_CORTE y CONSOLIDADO
    if (!deptDb || deptDb === 'SALA_CORTE') {
      componentesCobertura?.push({
        nombre: 'Ventas Sala de Corte',
        estado: ventasSala > 0 ? 'COMPLETO' : 'PENDIENTE',
        fuente: 'ldd_rentabilidad_control + servicios_corte',
      });
    }

    // Ventas Tienda: PENDIENTE hasta Epelsa — solo para TIENDA y CONSOLIDADO
    if (!deptDb || deptDb === 'TIENDA') {
      componentesCobertura?.push({
        nombre: 'Ventas Tienda',
        estado: 'PENDIENTE',
        fuente: 'Pendiente Epelsa',
        pendienteMsg: 'Pendiente Epelsa',
      });
      componentesCobertura?.push({
        nombre: 'COGS Tienda',
        estado: 'PENDIENTE',
        fuente: 'Pendiente Epelsa',
        pendienteMsg: 'Pendiente Epelsa',
      });
    }

    // Distribución: PENDIENTE hasta Contasimple — solo para DISTRIBUCION y CONSOLIDADO
    if (!deptDb || deptDb === 'DISTRIBUCION') {
      componentesCobertura?.push({
        nombre: 'Distribución',
        estado: 'PENDIENTE',
        fuente: 'Pendiente Contasimple',
        pendienteMsg: 'Pendiente Contasimple',
      });
    }

    // Amortizaciones: siempre PENDIENTE
    componentesCobertura?.push({
      nombre: 'Amortizaciones',
      estado: 'PENDIENTE',
      fuente: 'Pendiente módulo activos',
      pendienteMsg: 'Pendiente',
    });

    const completos = componentesCobertura?.filter(c => c?.estado === 'COMPLETO')?.length;
    const coberturaPct = componentesCobertura?.length > 0
      ? Math.round((completos / componentesCobertura?.length) * 100)
      : 0;

    return {
      // Ingresos
      ventasSala,
      ventasTienda,
      ventasDistribucion,
      otrosIngresos,
      totalIngresos,
      // Estado de fuentes
      tiendaPendiente,
      distribucionPendiente,
      salaConDatos,
      // COGS
      cogsSala,
      cogsTienda,
      cogsSalaPendiente,
      cogsTiendaPendiente,
      cogsDistribucionPendiente,
      cogsPendiente,
      totalCOGS,
      // Margen bruto
      margenBruto,
      margenBrutoPendiente,
      margenBrutoPct: totalIngresos > 0 ? (margenBruto / totalIngresos) * 100 : 0,
      // Personal
      salariosSala: salariosSalaFinal,
      ssEmpresa,
      otroPersonal,
      totalPersonal,
      salariosSalaFuente,
      // Gastos operativos
      alquiler, electricidad, agua, gestoria, seguros, software, transporte, publicidad, otrosGastos,
      totalGastosOperativos,
      // Resultado operativo
      resultadoOperativo,
      resultadoOperativoPct: totalIngresos > 0 ? (resultadoOperativo / totalIngresos) * 100 : 0,
      // Gastos financieros
      comisionesBancarias,
      totalGastosFinancieros,
      gastosFinancierosConFuente,
      // Resultado antes de impuestos
      resultadoAnteImpuestos,
      // Cobertura
      coberturaPct,
      componentesCobertura,
      // Detalle gastos para centros de coste
      costosFijosDetalle: detalleGastos,
    };
  },

  // Centros de coste con ingresos y costes asociados
  getCentrosCosteDetalle: async ({ period = 'month', dateFrom, dateTo } = {}) => {
    const { from, to } = buildDateRange(period, dateFrom, dateTo);
    const toNext = new Date(to); toNext?.setDate(toNext?.getDate() + 1);
    const toNextStr = toNext?.toISOString()?.split('T')?.[0];

    const { data: centros } = await supabase?.from('fin_centros_coste')?.select('*')?.order('nombre');
    const { data: gastos } = await supabase?.from('fin_gastos')
      ?.select('centro_coste_id,base_imponible,total,tipo')
      ?.gte('fecha', from)?.lt('fecha', toNextStr)
      ?.eq('es_prevision', false)?.neq('estado', 'CANCELADO');
    const { data: facturas } = await supabase?.from('fin_facturas')
      ?.select('centro_coste_id,total')
      ?.gte('fecha', from)?.lt('fecha', toNextStr)?.eq('tipo', 'EMITIDA');

    const totalGastos = (gastos || [])?.reduce((s, g) => s + fmt(g?.base_imponible), 0);
    const totalIngresos = (facturas || [])?.reduce((s, f) => s + fmt(f?.total), 0);

    return (centros || [])?.map(c => {
      const gastosCC = (gastos || [])?.filter(g => g?.centro_coste_id === c?.id);
      const facturasCC = (facturas || [])?.filter(f => f?.centro_coste_id === c?.id);
      const costes = gastosCC?.reduce((s, g) => s + fmt(g?.base_imponible), 0);
      const ingresos = facturasCC?.reduce((s, f) => s + fmt(f?.total), 0);
      const resultado = ingresos - costes;
      const pctSobreTotal = totalGastos > 0 ? (costes / totalGastos) * 100 : 0;
      return {
        id: c?.id,
        nombre: c?.nombre,
        tipo: c?.tipo,
        ingresos,
        costes,
        resultado,
        pctSobreTotal,
      };
    });
  },
};

// ─── AUDITORÍA ───────────────────────────────────────────────────────────────

export const auditoriaFinancieraService = {
  log: async ({ accion, tablaAfectada, registroId, usuarioEmail, valoresAnteriores, valoresNuevos, descripcion }) => {
    try {
      await supabase?.from('fin_auditoria')?.insert({
        accion, tabla_afectada: tablaAfectada, registro_id: registroId,
        usuario_email: usuarioEmail, valores_anteriores: valoresAnteriores,
        valores_nuevos: valoresNuevos, descripcion,
      });
    } catch (_) {}
  },

  getRecent: async ({ limit = 100, tablaAfectada, accion, search, dateFrom, dateTo } = {}) => {
    let query = supabase?.from('fin_auditoria')?.select('*')?.order('created_at', { ascending: false })?.limit(limit);
    if (tablaAfectada && tablaAfectada !== 'Todos') query = query?.eq('tabla_afectada', tablaAfectada);
    if (accion && accion !== 'Todos') query = query?.eq('accion', accion);
    if (dateFrom) query = query?.gte('created_at', dateFrom);
    if (dateTo) query = query?.lt('created_at', dateTo + 'T23:59:59.999Z');
    if (search) query = query?.or(`descripcion.ilike.%${search}%,usuario_email.ilike.%${search}%,tabla_afectada.ilike.%${search}%`);
    const { data, error } = await query;
    if (error) throw error;
    return data || [];
  },

  getKPIs: async () => {
    const hoy = new Date()?.toISOString()?.split('T')?.[0];
    const { data: hoyData } = await supabase?.from('fin_auditoria')
      ?.select('id,accion,usuario_email')?.gte('created_at', hoy);
    let rows = hoyData || [];
    const usuarios = new Set(rows?.map(r => r?.usuario_email)?.filter(Boolean));
    return {
      registrosHoy: rows?.length,
      usuariosActivos: usuarios?.size,
      creaciones: rows?.filter(r => r?.accion === 'CREACION')?.length,
      ediciones: rows?.filter(r => r?.accion === 'EDICION')?.length,
      eliminaciones: rows?.filter(r => r?.accion === 'ELIMINACION')?.length,
      confirmacionesOCR: rows?.filter(r => r?.accion === 'CONFIRMACION_OCR')?.length,
    };
  },
};

export { buildDateRange, calcularCosteMensualNormalizado, calcularProximoVencimiento, mapDeptToDb };

Object.assign(dashboardFinancieroCorpServiceRef, dashboardFinancieroCorpService);